import React, { useEffect, useRef, useState } from "react";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { View, Text } from "../../../components/shared/Themed";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import {
  Dimensions,
  Image,
  TouchableOpacity,
  StyleSheet,
  ImageSourcePropType,
  Modal,
} from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { ScrollView } from "react-native-gesture-handler";
import { ActivityIndicator } from "react-native-paper";
import { validSignedURLWithExt } from "../../../utils/validateSignedUrl";
import { translateFields, useTranslation } from "../../../utils/useTranslation";
import { stockReportTranslationMap } from "../../../utils/translations";
import { useSelector } from "react-redux";

interface OrderItem {
  OrderNumber1?: string;
  ItemCode?: string;
  FrgnName?: string;
  ItmsGrpCod?: string;
  ItemName?: string;
  Location?: string;
  PerCtns?: number;
  PerPallet?: number;
  Available2?: string;
  AvaliablePallet?: string;
  OnWater?: string;
  OnOrder2?: string;
  CardCode?: string;
  CardName?: string;
  InPallets?: string;
  image?: string;
  PicturName?: string;
}

interface RouteParams {
  order: OrderItem;
}

interface DetailItem {
  label: string;
  value: string;
  supportingValue?: string;
}

type SpecialLabels = {
  [key: string]: {
    oldLabel: string;
    newLabel: string;
    newValue?: string;
    supportingValue?: string;
  };
};

const StockReportDetailsReport = () => {
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors);
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const { order } = route.params || { order: {} };
  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width,
  );
  const navigation = useNavigation();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(false);
  const [isFullScreenImage, setIsFullScreenImage] = useState(false);
  const [isDisplayViewOn, setIsDisplayViewOn] = useState(false);
  const { t } = useTranslation();
  const { language } = useSelector((state: any) => state.configurations);

  // Determine if we're on a small screen (mobile)
  const isMobileView = windowWidth < 768;

  useEffect(() => {
    // Redirect if order is invalid
    if (!order || typeof order === "string") {
      navigation.goBack();
      return;
    }

    // Track screen size changes
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    });

    // Cleanup on unmount
    return () => subscription?.remove();
  }, [order, navigation]);

  useEffect(() => {
    navigation.setOptions({ title: t("stockReportDetails") });
  }, [navigation, language, t]);

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const handleImageLoadStart = () => {
    setIsImageLoading(true);
  };

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerHeaderStyle}>
        <View style={styles.headerRow}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <AntDesign name="arrowleft" size={24} color={colors.text} />
          </TouchableOpacity>

          <View style={styles.headerTitleContainer}>
            <Text selectable={true} style={styles.headerTitle}>
              {`${t("itemCode")}: ${order?.ItemCode || ""}`}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const ProductDetails = () => {
    const excludedKeys = [
      "ItemCode",
      "InPallets",
      "FrgnName",
      "CardCode",
      "CardName",
      "ItmsGrpCod",
    ];

    const rawAvailable = Number(order.OnHand) - order.Quantity - order.Delivery;
    const pieces = rawAvailable < 0 ? 0 : rawAvailable;
    const cartons = order.PerCtns ? pieces / order.PerCtns : 0;
    const pallets = order.PerCtns && order.PerPallet ? Math.round(pieces / order.PerCtns / order.PerPallet) : 0;

    const orderedKeys = [
      "ItemName",
      "Location",
      "PerCtns",
      "PerPallet",
      "calculated_pieces",
      "calculated_cartons",
      "calculated_pallets",
      "OrderReserved",
      "OnWater",
      "OnOrder2",
      "Inventory Transferring",
    ];

    const specialLabels: SpecialLabels = {
      PerPallet: {
        oldLabel: "PerPallet",
        newLabel: t("totalPerPallet"),
        newValue:
          order.PerPallet && order.PerCtns
            ? (order.PerPallet * order.PerCtns).toLocaleString()
            : "-",
        supportingValue:
          order.PerPallet && order.PerCtns ? order.PerPallet.toString() : "-",
      },
      ItmsGrpCod: {
        oldLabel: "ItmsGrpCod",
        newLabel: t("display"),
        newValue: isDisplayViewOn ? t("perCartonLabel") : t("perPieceLabel"),
      },
      calculated_pieces: {
        oldLabel: "calculated_pieces",
        newLabel: t("piece"),
        newValue: pieces.toLocaleString(),
      },
      calculated_cartons: {
        oldLabel: "calculated_cartons",
        newLabel: t("carton"),
        newValue: Number(cartons.toFixed(1)).toLocaleString(),
      },
      calculated_pallets: {
        oldLabel: "calculated_pallets",
        newLabel: t("pallets"),
        newValue: Number(pallets.toFixed(1)).toLocaleString(),
      },
    };

    const detailItems: (DetailItem & { key: string })[] = orderedKeys
      .filter((key) => !excludedKeys.includes(key))
      .map((key) => {
        if (specialLabels[key]) {
          const special = specialLabels[key];
          return {
            key,
            label: special.newLabel,
            value: special.newValue || order?.[key]?.toString() || "-",
            supportingValue: special.supportingValue,
          };
        } else {
          const rawValue = (order as any)?.[key];
          const isNumericField = ["OrderReserved", "OnWater", "OnOrder2", "Inventory Transferring"].includes(key);
          const formattedValue = isNumericField && rawValue !== undefined && rawValue !== null && rawValue !== "" && !isNaN(Number(rawValue))
            ? Number(rawValue).toLocaleString()
            : rawValue?.toString() || "-";

          return {
            key,
            label: (translateFields(key, t, stockReportTranslationMap) || key).replace(/\n/g, " "),
            value: formattedValue,
          };
        }
      });

    const getGroup = (key: string) => {
      if (["ItemCode", "FrgnName", "CardCode", "CardName", "ItmsGrpCod", "ItemName", "Location", "PerCtns", "PerPallet"].includes(key)) return t("identifiers");
      if (["calculated_pieces", "calculated_cartons", "calculated_pallets"].includes(key)) return t("quantities");
      if (["OrderReserved", "OnWater", "OnOrder2", "Inventory Transferring"].includes(key)) return t("availability");
      return t("otherInformation");
    };

    const groupedItems = detailItems.reduce((acc, item) => {
      const group = getGroup(item.key);
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {} as Record<string, typeof detailItems>);

    const groupOrder = [
      t("identifiers"),
      t("quantities"),
      t("availability"),
      t("otherInformation"),
    ];

    const sortedGroups = Object.keys(groupedItems).sort((a, b) => {
      return groupOrder.indexOf(a) - groupOrder.indexOf(b);
    });

    return (
      <View style={styles.productDetailsContainer}>
        <View style={styles.header}>
          <Text selectable={true} style={styles.title}>
            {order?.ItemCode || ""}
          </Text>
          <Text selectable={true} style={styles.subtitle}>
            {order?.FrgnName || ""}
          </Text>
        </View>
        <View style={styles.body}>
          {sortedGroups.map((groupName) => (
            <View key={groupName} style={{ marginBottom: 16 }}>
              <View
                style={{
                  backgroundColor: isDark ? "#2A2B2E" : "#F8FAFC",
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 6,
                  marginBottom: 4,
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    color: isDark ? "#9CA3AF" : "#475569",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {groupName}
                </Text>
              </View>
              <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                {groupedItems[groupName].map((item) => (
                  <View
                    key={item.key}
                    style={{
                      width: "50%",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      paddingVertical: 12,
                      paddingHorizontal: 8,
                      borderBottomWidth: 1,
                      borderBottomColor: isDark ? "#374151" : "#F1F5F9",
                    }}
                  >
                    <Text
                      selectable={true}
                      style={{
                        fontSize: 10,
                        color: isDark ? "#9CA3AF" : "#64748B",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        marginBottom: 6,
                      }}
                    >
                      {item.label}
                    </Text>
                    <View style={{ flexDirection: "row", alignItems: "baseline" }}>
                      <Text
                        selectable={true}
                        style={{
                          fontSize: 14,
                          color: isDark ? "#F9FAFB" : "#0F172A",
                          fontWeight: "500",
                        }}
                      >
                        {item.value?.toLocaleString()}
                      </Text>
                      {item.supportingValue && (
                        <Text selectable={true} style={styles.supporting}>
                          ({item.supportingValue?.toLocaleString()})
                        </Text>
                      )}
                    </View>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const prevItemCodeRef = useRef<any>();

  useEffect(() => {
    if (order.PicturName !== prevItemCodeRef.current) {
      prevItemCodeRef.current = order.PicturName;
      validSignedURLWithExt(
        order.PicturName || "",
        "label",
        setIsImageLoading,
        setSelectedImage,
      );
    }
  }, [order.PicturName]);

  const ProductImage = () => {
    return (
      <View style={styles.imageContainer}>
        {isImageLoading && (
          <ActivityIndicator size="large" style={styles.loader} />
        )}
        <TouchableOpacity
          style={styles.productImage}
          onPress={() => setIsFullScreenImage(true)}
          activeOpacity={0.8}
        >
          <Image
            source={{ uri: selectedImage } as ImageSourcePropType}
            style={{ width: "100%", height: "100%" }}
            resizeMode="contain"
          />
        </TouchableOpacity>

        <Modal
          visible={isFullScreenImage}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setIsFullScreenImage(false)}
        >
          <View style={styles.fullScreenImageContainer}>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={() => setIsFullScreenImage(false)}
            >
              <AntDesign name="close" size={28} color="#fff" />
            </TouchableOpacity>
            <Image
              source={{ uri: selectedImage } as ImageSourcePropType}
              style={styles.fullScreenImage}
              resizeMode="contain"
            />
          </View>
        </Modal>
      </View>
    );
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <DrawerHeader />

      <View
        style={[
          styles.contentContainer,
          isMobileView && styles.contentContainerMobile,
        ]}
      >
        {/* Left Side - Order Details */}
        <View
          style={[
            styles.orderDetails,
            isMobileView ? styles.orderDetailsMobile : null,
          ]}
        >
          <View style={[styles.detailBox, isMobileView && styles.detailBoxMobile]}>
            <ProductDetails />
          </View>
        </View>

        {/* Middle vertical line - only shown in desktop view */}
        {!isMobileView && <View style={styles.verticalLine} />}

        {/* Right Side - Product Image */}
        <View
          style={[
            styles.imageSection,
            isMobileView ? styles.imageSectionMobile : null,
          ]}
        >
          <View style={[styles.detailBox, isMobileView && styles.detailBoxMobile]}>
            <ProductImage />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

const createStyles = (colors: any) => StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: colors.background,
  },
  contentContainer: {
    padding: 16,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  contentContainerMobile: {
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  orderDetails: {
    flex: 1,
    maxWidth: "48%",
  },
  orderDetailsMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    maxWidth: "100%",
    marginBottom: 16,
  },
  imageSection: {
    flex: 1,
    maxWidth: "48%",
  },
  imageSectionMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    maxWidth: "100%",
  },
  detailBox: {
    borderRadius: 14,
    padding: 16,
    flex: 1,
  },
  detailBoxMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  verticalLine: {
    width: 1,
    backgroundColor: colors.border,
    marginHorizontal: 16,
  },
  drawerHeaderStyle: {
    width: "100%",
    zIndex: 999,
    backgroundColor: colors.surface,
    height: 56,
    borderBottomWidth: 1,
    borderColor: colors.border,
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    paddingVertical: 8,
    paddingRight: 16,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontWeight: "700",
    fontSize: 16,
  },
  // Product Details Styles
  productDetailsContainer: {
  },
  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 4,
    color: colors.text,
  },
  subtitle: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 16,
  },
  body: {
  },
  label: {
    fontWeight: "600",
    fontSize: 14,
    flex: 1,
    color: colors.text,
  },
  value: {
    fontSize: 14,
    color: colors.text,
    flex: 1,
    textAlign: "right",
  },
  supporting: {
    fontSize: 12,
    color: colors.textMuted,
    marginLeft: 8,
  },
  // Image Styles
  imageContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    minHeight: 250,
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
  productImage: {
    width: "100%",
    height: "100%",
    flex: 1,
    minHeight: 250,
  },
  loader: {
    position: "absolute",
    zIndex: 1,
  },
  fullScreenImageContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    justifyContent: "center",
    alignItems: "center",
  },
  fullScreenImage: {
    width: "100%",
    height: "100%",
  },
  closeButton: {
    position: "absolute",
    top: 50,
    right: 20,
    zIndex: 2,
    padding: 10,
  },
});

export default StockReportDetailsReport;
